import { useEffect, useState } from "react";
import { Button, Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

import { useLoadData } from "../../../../hooks/useLoadData";
import { useSaveData } from "../../../../hooks/useSaveData";
import { ApiResponse } from "../../../../models/api-response";
import { DetailsFormComponent } from "./details-form.component";
import { useReactHookForm } from "../../../../hooks/useReactHookForm";
import { PlanDetailsComponent } from "./plan-details/plan-details.component";
import { planReviewApiKeyStore } from "../../../../api/plan-review-api-key-store";
import { ReactHookForm } from "../../../../components/hook-form/react-hook-form.component";
import { ContactDetailsComponent } from "./contact-details/contact-details.component";
import { PlanReviewTaskSelectResult } from "./plan-review-task-select-result.component";
import { FileUploadComponent } from "./file-upload/file-upload.component";
import { useSnackbar } from "../../../../hooks/use-snackbar.hook";
import { GuardedComponent } from "../../../../components/guarded-component";
import { UserPermissionName } from "../../../../models/user-permission-name";
import { useAuthorize } from "../../../../hooks/use-authorize";
import dayjs from "dayjs";
import { set } from "date-fns";

const isPlanEnabled = process.env.REACT_APP_PLAN_FLAG === 'true';

const downloadBlob = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const handleFileFunctionality = (fileResponse: any, fileData: any) => {
    if (fileResponse) {
        if (typeof fileResponse === 'string') {
            const blob = new Blob([new Uint8Array(
                Array.from(atob(fileResponse), (char) => char.charCodeAt(0)))],
                { type: fileData?.fileType });
            downloadBlob(blob, fileData?.fileName ?? 'file');
        } else {
            throw new Error('Invalid data type for file download.');
        }
    }
}

const styling = {
    dialogTitleStyle: { padding: '16px 24px 0' },
    contentPadding: { paddingTop: '30px' },
};

interface PlanReviewTaskDialog {
    title: string;
    planReviewData: any;
    moduleCode: string;
    planReviewSchema: any;
    onSubmit: () => void;
    onClosed: () => void;
}

export const PlanReviewTaskSelectDialog = ({ title, planReviewData, moduleCode, planReviewSchema, onSubmit, onClosed }: PlanReviewTaskDialog) => {

    const form = useReactHookForm({
        defaultValues: {
            workOrderNumber: "",
            customerName: "",
            officeName: "",
            projectName: "",
            unit: "",
            address: "",
            jobsite: "",
            currentStatus: "",
            serviceGroupName: "",
            statusCode: ""
        },
        yupSchema: planReviewSchema
    });

    const { showSnackbar } = useSnackbar();

    const [file, setFile] = useState<any>();
    const [selectedPlanData, setSelectedPlanData] = useState<any>();
    const [selectContactData, setSelectedContactData] = useState<any>();
    const [selectFileData, setSelectedFileData] = useState<any>();
    const [planFileOrders, setPlanFileOrder] = useState<any[]>();
    const [fileOrders, setFileOrder] = useState<any[]>();
    const [status, setStatus] = useState<string>();
    const [isDisabledDownload, seDisabledDownload] = useState<boolean>(false);
    const [customFileName, setCustomFileName] = useState<any>("");
    const [customFileError, setCustomFileError] = useState<boolean>(false);

    const addTimeStampInFileName = () => {
        // const fileName = `Plan_Review_File_${dayjs().format('MMDDYYYY_HHmmss')}`;
        const fileName = ``;
        setCustomFileName(fileName)
    }

    useEffect(() => { addTimeStampInFileName() }, [])

    const { data, isLoading, isFetching } = useLoadData<ApiResponse<any>>(
        planReviewApiKeyStore.getPlanReviewTask(planReviewData?.planReviewTaskID),
        !!planReviewData?.planReviewTaskID && planReviewData?.planReviewTaskID > 0
    );

    const { isLoading: isFilesLoading, mutateAsync: mutateFileAsync } = useSaveData<any>(planReviewApiKeyStore.combinedPDFDownload());

    const { isLoading: isReviewTaskLoading, mutateAsync } = useSaveData<any>(planReviewApiKeyStore.updatePlanReviewTask());

    useEffect(() => {
        if (data) {
            const taskData = data?.data;
            form.reset({
                workOrderNumber: taskData?.workOrderNumber,
                customerName: taskData?.customerName,
                officeName: taskData?.officeName,
                projectName: taskData?.projectName,
                unit: taskData?.unitName,
                address: taskData?.address,
                jobsite: taskData?.jobSite,
                currentStatus: taskData?.planReviewTaskStatus == 'Complete' ? "Complete and Send Mail" : taskData?.planReviewTaskStatus,
                serviceGroupName: taskData?.serviceGroupName,
                statusCode: taskData?.planReviewTaskStatusCode
            })
        }
    }, [data])

    useEffect(() => {
        if (selectedPlanData?.length > 0 || selectFileData?.length > 0) seDisabledDownload(true);
        else seDisabledDownload(false);
    }, [selectedPlanData, selectFileData])

    useEffect(() => {
        if (!isDisabledDownload) addTimeStampInFileName();
    }, [isDisabledDownload])

    const submitHanlder = async (value: any) => {
        if (customFileName == "" || customFileName == null) {
            setCustomFileError(true);
            showSnackbar(`File name is required.`, 'error');
            return;
        }
        const formData = new FormData();
        formData.append('PlanReviewTaskID', planReviewData?.planReviewTaskID ?? '');
        selectedPlanData?.forEach((plan: any) => {
            const item = {
                ItemID: plan?.itemType === "Plan" ? plan?.planID : plan?.fileID,
                ItemType: plan?.itemType,
                FileOrder: plan?.fileOrder ?? 0
            };
            formData.append('FilesToMail', JSON.stringify(item));
        });
        selectContactData?.map((contact: any) => formData.append('ContactIDs', contact?.contactsID));
        selectFileData?.forEach((file: any) => {
            const item = {
                ItemID: file?.fileID,
                ItemType: 'File',
                FileOrder: file?.fileOrder ?? 0
            };
            formData.append('FilesToMail', JSON.stringify(item));
        });
        formData.append('PlanReviewTaskStatusCode', status ?? value?.statusCode);
        if (customFileName)
            formData.append('DownloadZipfileName', customFileName);
        await mutateAsync({ data: formData });
        onSubmit();
    };

    const handleFileData = (fileData: any) => { setFile(fileData) }

    const handlePlanData = (selectedPlans: any) => {
        setPlanFileOrder(selectedPlans?.map((item: any) => Number(item?.fileOrder ?? 0)))
        setSelectedPlanData(selectedPlans);
    }

    const handleContactData = (selectedContacts: any) => {
        setSelectedContactData(selectedContacts);
    }

    const removeExtension = (fileName: any) => {
        const lastDotIndex = fileName?.lastIndexOf(".");
        if (lastDotIndex === -1) return fileName; // no extension
        return fileName?.substring(0, lastDotIndex);
    };

    const handleSelectedFileData = (selectedFiles: any) => {
        if (selectedFiles?.length == 1) {
            setCustomFileName(removeExtension(selectedFiles[0]?.fileName));
        }
        else addTimeStampInFileName();
        setFileOrder(selectedFiles?.map((item: any) => Number(item?.fileOrder ?? 0)))
        setSelectedFileData(selectedFiles);
        setCustomFileError(false);
    }

    const handleStatus = (statusValue: string) => {
        setStatus(statusValue);
    }

    const handleFileName = (event: any) => {
        setCustomFileError(event?.target?.value == "" ? true : false);
        setCustomFileName(event?.target?.value ?? '')
    }

    const combinedData = (planData: any[], fileData: any[]) => {
        const safePlanData = Array.isArray(planData) ? planData : [];
        const safeFileData = Array.isArray(fileData) ? fileData : [];

        const transformedData = [
            ...safePlanData.map((item: any) => ({
                ItemID: item.fileID ?? item.planID,
                ItemType: "Plan",
                FileOrder: item.fileOrder,
            })),
            ...safeFileData.map((item: any) => ({
                ItemID: item.fileID,
                ItemType: "File",
                FileOrder: item.fileOrder,
            }))
        ];

        return transformedData;
    };

    const handleDownloadFiles = async () => {
        if (customFileName == "" || customFileName == null) {
            setCustomFileError(true);
            showSnackbar(`File name is required.`, 'error');
            return;
        }
        const hasInvalidPlanFileOrder = selectedPlanData?.some(
            (plan: any) => plan?.fileOrder === 0 || plan.fileOrder === null
        );
        const hasInvalidFileFileOrder = selectFileData?.some(
            (file: any) => file?.fileOrder === 0 || file.fileOrder === null
        );
        if (hasInvalidPlanFileOrder) {
            showSnackbar(`Please add the sort order to download files.`, 'error');
            return
        }
        if (hasInvalidFileFileOrder) {
            showSnackbar(`Please add the sort order to download files.`, 'error');
            return
        }

        const fileData = combinedData(selectedPlanData, selectFileData);
        const filePayload = {
            planReviewTaskID: planReviewData?.planReviewTaskID,
            downloadZipfileName: `${customFileName}.pdf`,
            files: fileData
        }
        const fileResponse = await mutateFileAsync({ data: filePayload });
        handleFileFunctionality(fileResponse, {
            fileName: `${customFileName}.pdf`,
            fileType: 'application/pdf'
        });
    }

    const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

    return (
        <Dialog
            open={true}
            fullWidth={true}
            maxWidth={'xl'}
            // onClose={handleClose}
            aria-labelledby="dialog-title"
            aria-describedby="dialog-description"
        >
            <DialogTitle id="dialog-title" style={styling.dialogTitleStyle}>
                {title}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClosed}
                sx={{
                    position: 'absolute',
                    right: 10,
                    top: 9,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <Tooltip title={<Typography variant="body1">Close</Typography>}>
                    <CloseIcon />
                </Tooltip>
            </IconButton>
            <DialogContent dividers style={styling.contentPadding} className="popupDilog">
                <ReactHookForm formName="PLAN_REVIEW_TASK_SELECT_FORM" onSubmit={submitHanlder} formContextProps={form}>
                    <Grid container spacing={2}>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <DetailsFormComponent isLoading={isLoading || isFetching || isReviewTaskLoading || isFilesLoading} />
                        </Grid>
                        {isPlanEnabled && (
                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                <PlanDetailsComponent
                                    title={title}
                                    planReviewTaskData={data?.data}
                                    fileOrder={fileOrders}
                                    selectedPlansData={handlePlanData}
                                    isLoading={isLoading || isFetching || isReviewTaskLoading || isFilesLoading} />
                            </Grid>
                        )}
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <ContactDetailsComponent
                                title={title}
                                planReviewTaskData={data?.data}
                                selectedContactsData={handleContactData}
                                isLoading={isLoading || isFetching || isReviewTaskLoading || isFilesLoading} />
                        </Grid>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <FileUploadComponent
                                title={title}
                                fileOrders={planFileOrders}
                                planReviewTaskData={data?.data}
                                selectedFileData={handleSelectedFileData}
                                parentLoading={isLoading || isFetching || isReviewTaskLoading || isFilesLoading}
                            />
                        </Grid>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <PlanReviewTaskSelectResult
                                moduleCode={moduleCode}
                                planReviewData={data?.data}
                                passFileData={handleFileData}
                                passStatusValue={handleStatus}
                                isParentLoading={isLoading || isFetching || isReviewTaskLoading || isFilesLoading} />
                        </Grid>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <div className="spaceBetween">
                                <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
                                    Cancel
                                </Button>
                                <div>
                                    {isDisabledDownload && (
                                        <>
                                            <TextField
                                                id="outlined-basic"
                                                label="File Name"
                                                variant="outlined"
                                                size="small"
                                                value={customFileName}
                                                onChange={handleFileName}
                                                helperText={customFileError && <p style={{ color: 'red' }}>File Name is required</p>}
                                                style={{ marginRight: 10, width: '300px' }}
                                            />
                                            <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
                                                <Button
                                                    type='button'
                                                    variant="contained"
                                                    onClick={handleDownloadFiles}
                                                    style={{ width: '130px', marginRight: 10 }}
                                                    disabled={isFilesLoading}>
                                                    DOWNLOAD
                                                </Button>
                                            </GuardedComponent>
                                        </>
                                    )}
                                    <Button type='submit' variant="contained" style={{ width: '130px' }}
                                        disabled={isReviewTaskLoading || isReadOnlyPermission}>
                                        Save
                                    </Button>
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </ReactHookForm>
            </DialogContent>
        </Dialog>
    );
}